import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../hooks/useAuth'
import styles from './LoginPage.module.css'

export function RegisterPage() {
  const { t } = useTranslation()
  const { register } = useAuth()
  const [form, setForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    companyName: '',
    companySlug: '',
  })
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const update = (key: string, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }))
    // Auto-generate slug from company name
    if (key === 'companyName') {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
      setForm(prev => ({ ...prev, companyName: value, companySlug: slug }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)

    const err = await register(form)
    if (err) setError(err)

    setSubmitting(false)
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.logo}>MIREY</div>
        <p className={styles.tagline}>{t('auth.register_subtitle')}</p>

        {error && <div className={styles.error}>{error}</div>}

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.row}>
            <label className={styles.label}>
              {t('auth.first_name')}
              <input
                type="text"
                className={styles.input}
                value={form.firstName}
                onChange={e => update('firstName', e.target.value)}
                required
                disabled={submitting}
              />
            </label>
            <label className={styles.label}>
              {t('auth.last_name')}
              <input
                type="text"
                className={styles.input}
                value={form.lastName}
                onChange={e => update('lastName', e.target.value)}
                required
                disabled={submitting}
              />
            </label>
          </div>

          <label className={styles.label}>
            {t('auth.email')}
            <input
              type="email"
              className={styles.input}
              value={form.email}
              onChange={e => update('email', e.target.value)}
              required
              autoComplete="email"
              disabled={submitting}
            />
          </label>

          <label className={styles.label}>
            {t('auth.password')}
            <input
              type="password"
              className={styles.input}
              value={form.password}
              onChange={e => update('password', e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              disabled={submitting}
            />
          </label>

          <label className={styles.label}>
            {t('auth.company_name')}
            <input
              type="text"
              className={styles.input}
              value={form.companyName}
              onChange={e => update('companyName', e.target.value)}
              required
              disabled={submitting}
            />
          </label>

          <label className={styles.label}>
            {t('auth.company_slug')}
            <input
              type="text"
              className={styles.input}
              value={form.companySlug}
              onChange={e => update('companySlug', e.target.value)}
              required
              pattern="[a-z0-9-]+"
              disabled={submitting}
            />
          </label>

          <button type="submit" className={styles.button} disabled={submitting}>
            {submitting ? t('common.loading') : t('auth.create_account')}
          </button>
        </form>

        <p className={styles.switchLink}>
          {t('auth.have_account')}{' '}
          <Link to="/login">{t('auth.login')}</Link>
        </p>
      </div>
    </div>
  )
}
